"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { AlertCircle, Home, Calculator, ArrowRight } from "lucide-react";
import { analyzeCalculatorRequest } from "@/lib/utils/calculator-redirect-helper";
import { useEffect, useState } from "react";

export default function CalculatorNotFound() {
  const pathname = usePathname();
  const [suggestion, setSuggestion] = useState<{
    exists: boolean;
    calculatorName?: string;
    correctCountries?: Array<{ code: string; name: string; flag: string }>;
    requestedCountry?: string;
    requestedCountryName?: string;
    category?: string;
    slug?: string;
  } | null>(null);

  useEffect(() => {
    // Parse the pathname to extract country, category, and slug
    const parts = pathname?.split("/").filter(Boolean);
    if (parts && parts.length >= 3) {
      const [country, category, slug] = parts;
      const result = analyzeCalculatorRequest(country, category, slug);
      setSuggestion(result);
    }
  }, [pathname]);

  // Show wrong country suggestion if calculator exists for other countries
  if (suggestion?.exists && suggestion.correctCountries && suggestion.correctCountries.length > 0) {
    return (
      <div className="not-found-container container py-16 text-center anim-fade-in">
        <div className="card error-card">
          <AlertCircle size={48} className="error-icon" />
          <h1 className="error-title">Calculator Not Available for {suggestion.requestedCountryName || "This Country"}</h1>
          <p className="error-description">
            The <strong>{suggestion.calculatorName}</strong> is a region-specific calculator that is not available for {suggestion.requestedCountryName || "the country you selected"}.
          </p>

          <div className="divider"></div>

          <h2 className="links-heading">This Calculator is Available For:</h2>
          <div className="links-grid" style={{ maxWidth: "500px", margin: "0 auto" }}>
            {suggestion.correctCountries.map((country) => (
              <Link
                key={country.code}
                href={`/${country.code}/${suggestion.category}/${suggestion.slug}`}
                className="btn btn-primary"
                style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "center", 
                  gap: "8px",
                  fontSize: "var(--text-base)"
                }}
              >
                <span style={{ fontSize: "var(--text-xl)" }}>{country.flag}</span>
                <span>{country.name}</span>
                <ArrowRight size={16} />
              </Link>
            ))}
          </div>

          <div className="divider"></div>

          <h2 className="links-heading">Or Explore Other Options</h2>
          <div className="links-grid">
            <Link href={`/${suggestion.requestedCountry}`} className="btn btn-secondary">
              <Calculator size={16} /> Browse All {suggestion.requestedCountryName} Calculators
            </Link>
            <Link href="/" className="btn btn-secondary">
              <Home size={16} /> Go to Homepage
            </Link>
          </div>

          <div className="quick-links">
            <Link href="/about" className="quick-link">About Us</Link>
            <Link href="/contact" className="quick-link">Contact Support</Link>
            <Link href="/blog" className="quick-link">Blog</Link>
          </div>
        </div>
      </div>
    );
  }

  // Generic 404 for calculators that don't exist at all
  return (
    <div className="not-found-container container py-16 text-center anim-fade-in">
      <div className="card error-card">
        <AlertCircle size={48} className="error-icon" />
        <h1 className="error-title">Calculator Not Found</h1>
        <p className="error-description">
          Sorry, we couldn't find the calculator you are looking for. It may have been moved, renamed, or doesn't exist.
        </p>

        <div className="divider"></div>

        <h2 className="links-heading">What You Can Do</h2>
        <div className="links-grid">
          {suggestion?.requestedCountry && (
            <Link href={`/${suggestion.requestedCountry}`} className="btn btn-primary">
              <Calculator size={16} /> Browse All {suggestion.requestedCountryName || "Available"} Calculators
            </Link>
          )}
          <Link href="/" className="btn btn-secondary">
            <Home size={16} /> Go to Homepage
          </Link>
        </div>

        <div className="quick-links">
          <Link href="/about" className="quick-link">About Us</Link>
          <Link href="/contact" className="quick-link">Contact Support</Link>
          <Link href="/blog" className="quick-link">Blog</Link>
        </div>
      </div>
    </div>
  );
}
